// Wohler Chemical — Master RC Stub Library
// Every compound from the master RC sheet (ch_categ.docx) that is not yet
// richly populated elsewhere is added here as a minimal stub so the full
// pipeline is visible on the site and in the Journal/To-Do.
//
// Each stub includes: id, name, fullName, categoryId, status=RESEARCH-NEEDED,
// evidenceConfidence=LOW, benefit/harm placeholders, summary, and nextSteps.
//
// Work these down from the /journal page as the team performs full research.

function stub({
  id, name, fullName, aliases = [], categoryId, program,
  summary, schedulingStatus = "unscheduled", schedulingNote = "",
  flag = "", benefit = 60, harm = 30
}) {
  return {
    id, name, fullName, aliases, categoryId, program,
    cas: null, formula: "TBD", molecularWeight: "TBD",
    schedulingStatus,
    schedulingNote: schedulingNote || "Scheduling status pending confirmation by regulatory counsel. Research-use-only posture required.",
    benefitScore: benefit,
    harmScore: harm,
    evidenceConfidence: "LOW",
    status: "RESEARCH-NEEDED",
    statusColor: benefit - harm > 40 ? "green" : benefit - harm > 10 ? "orange" : "red",
    goDecision: "TODO — added from master RC list, pending full research buildout",
    priorityTier: "Tier 3",
    summary: summary + (flag ? `  Scheduling flag: ${flag}.` : ""),
    pros: ["Listed on Wohler master RC research sheet"],
    cons: ["Research content pending — full buildout required"],
    pharmacology: { targets: [] },
    labSynthesis: null,
    kgScaleSynthesis: null,
    safety: { sideEffects: ["Pending research"] },
    publishedResearch: { keyFindings: ["Pending literature review"] },
    wohlerScoring: {
      benefitRationale: "Placeholder — needs literature review",
      harmRationale: "Placeholder — needs EHS / abuse-liability review",
      evidenceBasis: "LOW — stub entry pending full research"
    },
    nextSteps: [
      "Complete literature review and populate publishedResearch section",
      "Develop lab-scale synthesis route using Wohler standard solvents",
      "Design kg-scale manufacturing plan with EHS assessment",
      "Populate Wohler scoring (benefit, harm, evidence confidence)",
      "Confirm federal & state scheduling status with regulatory attorney",
      "Identify primary vendor / starting-material sources"
    ]
  };
}

export const masterStubCompounds = {

  // ========== FLAGSHIP: 4-AcO-MET (Wohler primary RC) ==========
  "4-aco-met": {
    id: "4-aco-met",
    name: "4-AcO-MET",
    fullName: "4-Acetoxy-N-methyl-N-ethyltryptamine (Metocin / Metacetin)",
    aliases: ["4-Acetoxy-MET", "Metocin", "Metacetin"],
    categoryId: "tryptamines",
    program: "Program B — Tryptamine Space (Wohler flagship)",
    cas: "1432794-99-4",
    formula: "C15H20N2O2",
    molecularWeight: "260.33 g/mol",
    schedulingStatus: "unscheduled-federal",
    schedulingNote: "Not federally scheduled in the United States. Psilocybin/psilocin-class analog — Federal Analogue Act scrutiny is possible. State-level scheduling varies — confirm per jurisdiction. Research-use-only posture required.",
    benefitScore: 88,
    harmScore: 18,
    evidenceConfidence: "MEDIUM",
    status: "GO",
    statusColor: "green",
    goDecision: "GO — Wohler flagship tryptamine; prodrug to 4-HO-MET with improved stability and standardized dosing.",
    priorityTier: "Tier 1",
    summary: "4-AcO-MET is Wohler Chemical's flagship tryptamine research compound — the O-acetylated prodrug of 4-HO-MET. Plasma deacetylation unmasks 4-HO-MET, a potent 5-HT2A/5-HT2C agonist. Shorter duration than psilocybin (~4–5 h) enables tighter clinical-research session design. Primary platform for depression, PTSD, end-of-life-distress, addiction, and neuroplasticity research at Wohler.",
    pros: [
      "Wohler flagship — existing synthetic route, analytical standards, and scale-up history",
      "Prodrug strategy improves stability vs free-base 4-HO-MET",
      "Shorter duration than psilocybin enables tighter clinical-research session design",
      "Strong university-partner appeal for 5-HT2A neuroplasticity research"
    ],
    cons: [
      "Psilocybin/psilocin analog — non-zero Federal Analogue Act scrutiny",
      "State-by-state scheduling varies",
      "Requires rigorous research-use-only controls"
    ],
    nextSteps: [
      "Populate full lab-synthesis block (indole → Grignard → oxalyl → amide → LAH → acetylation)",
      "Populate kg-scale synthesis plan using Wohler standard solvents (THF, EtOAc, Hexane, IPA)",
      "Populate pharmacology table: 5-HT2A Ki/Emax; 5-HT2C; 5-HT1A cross-binding",
      "Populate Wohler Scoring rationale and evidence-confidence tier",
      "Attach most-recent batch analytical data (NMR, LC-MS, purity)",
      "Add reference-grade published literature citations"
    ]
  },

  // ========== TRYPTAMINES (missing from existing files) ==========
  "4-ho-mpt": stub({
    id: "4-ho-mpt", name: "4-HO-MPT",
    fullName: "4-Hydroxy-N-methyl-N-propyltryptamine",
    categoryId: "tryptamines", program: "Program B — Tryptamine Space",
    summary: "Active metabolite / desacetyl comparator to 4-AcO-MPT. Serotonergic research compound in the 4-hydroxy tryptamine SAR series.",
    flag: "Similar to psilocin (Schedule I)"
  }),
  "4-ho-ept": stub({
    id: "4-ho-ept", name: "4-HO-EPT",
    fullName: "4-Hydroxy-N-ethyl-N-propyltryptamine",
    categoryId: "tryptamines", program: "Program B — Tryptamine Space",
    summary: "Active metabolite / desacetyl comparator to 4-AcO-EPT. High-interest receptor-profile candidate in the 4-hydroxy tryptamine SAR series.",
    flag: "Similar to psilocin (Schedule I)"
  }),
  "4-ho-dpt": stub({
    id: "4-ho-dpt", name: "4-HO-DPT",
    fullName: "4-Hydroxy-N,N-dipropyltryptamine",
    categoryId: "tryptamines", program: "Program B — Tryptamine Space",
    summary: "Active metabolite / desacetyl comparator to 4-AcO-DPT. Interesting for possible 5-HT2A/5-HT2C selectivity research.",
    flag: "Similar to psilocin (Schedule I)"
  }),
  "4-aco-mipt": stub({
    id: "4-aco-mipt", name: "4-AcO-MiPT",
    fullName: "4-Acetoxy-N-methyl-N-isopropyltryptamine",
    aliases: ["Mipracetin"],
    categoryId: "tryptamines", program: "Program B — Tryptamine Space",
    summary: "Prodrug of 4-HO-MiPT — an acetylated serotonergic tryptamine analog analogous to psilocybin's prodrug relationship to psilocin. Duration/profile comparator in the 4-hydroxy tryptamine SAR series.",
    flag: "Similar to psilocybin (Schedule I)"
  }),
  "5-meo-met": stub({
    id: "5-meo-met", name: "5-MeO-MET",
    fullName: "5-Methoxy-N-methyl-N-ethyltryptamine",
    categoryId: "tryptamines", program: "Program B — Tryptamine Space",
    summary: "5-MeO tryptamine comparator in the 5-methoxy SAR series. Mechanistically distinct from 4-hydroxy tryptamines (5-HT1A bias component).",
    flag: "Similar to 5-MeO-DMT class"
  }),
  "5-meo-dalt": stub({
    id: "5-meo-dalt", name: "5-MeO-DALT",
    fullName: "N,N-di(allyl)-5-methoxytryptamine",
    categoryId: "tryptamines", program: "Program B — Tryptamine Space",
    summary: "5-MeO tryptamine comparator. Di-allyl N-substitution provides distinct metabolic profile vs 5-MeO-DMT and 5-MeO-MiPT.",
    flag: "Similar to 5-MeO-DMT class"
  }),
  "norpsilocin-analogs": stub({
    id: "norpsilocin-analogs", name: "Norpsilocin Analogs",
    fullName: "Norpsilocin (4-HO-NMT) and related N-unsubstituted tryptamine analogs",
    categoryId: "tryptamines", program: "Program B — Tryptamine Space",
    summary: "Useful for 4-HO tryptamine SAR studies — active metabolite of baeocystin. Class entry for future expansion.",
    flag: "Similar to psilocin"
  }),
  "aeruginascin-analogs": stub({
    id: "aeruginascin-analogs", name: "Aeruginascin Analogs",
    fullName: "Quaternary N-trimethyl tryptamine analogs",
    categoryId: "tryptamines", program: "Program B — Tryptamine Space",
    summary: "Quaternary tryptamine class for entourage-effect research in mushroom alkaloid pharmacology. Less classical RC profile but important for kinetic understanding of mushroom extracts.",
    flag: "Similar pharmacology to psilocin class"
  }),

  // ========== MITRAGYNINE / MGM (missing) ==========
  "paynantheine": stub({
    id: "paynantheine", name: "Paynantheine",
    fullName: "Paynantheine — minor kratom alkaloid",
    categoryId: "mitragynine", program: "Program A — Kratom / Harm Reduction",
    summary: "Minor kratom alkaloid (~9% of total alkaloid content). Smooth muscle and opioid-system research reference.",
    flag: "Opioid-system similarity"
  }),
  "corynantheidine": stub({
    id: "corynantheidine", name: "Corynantheidine",
    fullName: "Corynantheidine — kratom alkaloid",
    categoryId: "mitragynine", program: "Program A — Kratom / Harm Reduction",
    summary: "Opioid antagonist-like kratom alkaloid. Interesting mechanistic opposite of mitragynine's partial MOR agonism.",
    flag: "Opioid-system similarity"
  }),
  "corynoxine-a": stub({
    id: "corynoxine-a", name: "Corynoxine A",
    fullName: "Corynoxine A — Uncaria alkaloid",
    categoryId: "mitragynine", program: "Program A — Kratom / Harm Reduction",
    summary: "Neuroprotective / autophagy research compound. Kratom-adjacent with lower opioid-receptor affinity than mitragynine — interesting for Parkinson's and neurodegeneration research.",
  }),
  "mitragynine-oxindole-analogs": stub({
    id: "mitragynine-oxindole-analogs", name: "Mitragynine Oxindole Analogs",
    fullName: "Mitragynine oxindole structural analog class",
    categoryId: "mitragynine", program: "Program A — Kratom / Harm Reduction",
    summary: "Oxindole-rearranged mitragynine analogs — includes mitragynine pseudoindoxyl class. Pain and neuroprotection research.",
    flag: "Opioid-system similarity"
  }),
  "fluorocorynantheidine-analogs": stub({
    id: "fluorocorynantheidine-analogs", name: "9-Fluorocorynantheidine Analogs",
    fullName: "9-Fluorocorynantheidine analog series",
    categoryId: "mitragynine", program: "Program A — Kratom / Harm Reduction",
    summary: "MGM-family expansion — fluorinated corynantheidine analogs targeting biased MOR activity with improved PK.",
    flag: "Opioid similarity"
  }),
  "mitragynine-ester-analogs": stub({
    id: "mitragynine-ester-analogs", name: "Mitragynine Ester Analogs",
    fullName: "Mitragynine ester prodrug analogs",
    categoryId: "mitragynine", program: "Program A — Kratom / Harm Reduction",
    summary: "Ester prodrug SAR program for tuning PK/oral bioavailability of mitragynine scaffold.",
    flag: "Opioid similarity"
  }),

  // ========== OPIOID / BIASED MOR (missing) ==========
  "sr-14968": stub({
    id: "sr-14968", name: "SR-14968",
    fullName: "SR-14968 — biased MOR research ligand",
    categoryId: "opioid", program: "Program D — Biased MOR / Safer Opioid",
    summary: "Related biased MOR research compound from the same series as SR-17018. Literature comparator for biased-agonism research.",
    flag: "High opioid similarity", benefit: 62, harm: 40
  }),
  "pzm21": stub({
    id: "pzm21", name: "PZM21",
    fullName: "PZM21 — G-protein-biased MOR agonist (Manglik 2016)",
    categoryId: "opioid", program: "Program D — Biased MOR / Safer Opioid",
    summary: "Structure-based virtual screening hit — G-protein-biased mu-opioid receptor agonist reported in Nature (Manglik 2016). Widely studied in biased-agonism research; subsequent studies called initial bias claims into question.",
    flag: "High opioid similarity", benefit: 65, harm: 38
  }),
  "oliceridine-comparator": stub({
    id: "oliceridine-comparator", name: "Oliceridine / TRV130 Comparator",
    fullName: "Oliceridine (TRV130) — FDA-approved biased MOR agonist",
    aliases: ["TRV130", "Olinvyk"],
    categoryId: "opioid", program: "Program D — Biased MOR / Safer Opioid",
    summary: "Approved biased MOR comparator (Olinvyk, 2020). Literature benchmark only — not a Wohler product target but an essential pharmacological reference.",
    schedulingStatus: "scheduled",
    schedulingNote: "SCHEDULE II (DEA) — FDA-approved opioid analgesic (Olinvyk, Trevena). Reference compound only.",
    flag: "Schedule II", benefit: 55, harm: 55
  }),
  "brorphine-warning-class": stub({
    id: "brorphine-warning-class", name: "Brorphine Warning Class",
    fullName: "Brorphine and related high-potency synthetic opioid research-risk class",
    categoryId: "opioid", program: "Program D — Biased MOR / Safer Opioid",
    summary: "NOT A TARGET — enforcement-risk comparator class included as a regulatory-intelligence reference. Brorphine-class compounds have been implicated in overdose deaths and emergency scheduling.",
    schedulingStatus: "scheduled",
    schedulingNote: "SCHEDULE I — DEA emergency scheduled 2020. Do NOT synthesize — included only as watch/risk reference.",
    flag: "HIGH SYNTHETIC OPIOID ENFORCEMENT CONCERN", benefit: 10, harm: 95
  }),
  "peripheral-kor-agonists": stub({
    id: "peripheral-kor-agonists", name: "Peripheral KOR Agonists",
    fullName: "Peripherally-restricted kappa-opioid receptor agonist class",
    categoryId: "opioid", program: "Program D — Biased MOR / Safer Opioid",
    summary: "KOR agonists with limited CNS penetration for pain, itch, and inflammation research without central dysphoria. Includes difelikefalin (Korsuva) comparator.",
    flag: "Opioid-receptor similarity", benefit: 70, harm: 25
  }),
  "cebranopadol-like": stub({
    id: "cebranopadol-like", name: "Cebranopadol-like NOP/MOR/DOR/KOR Ligands",
    fullName: "Multi-receptor opioid ligand class (NOP/MOR/DOR/KOR)",
    categoryId: "opioid", program: "Program D — Biased MOR / Safer Opioid",
    summary: "Strong analgesia research class with mixed opioid-receptor activity. Cebranopadol itself is in late clinical development for chronic pain.",
    flag: "Opioid-receptor similarity", benefit: 68, harm: 32
  }),
  "peptide-opioid-ligands": stub({
    id: "peptide-opioid-ligands", name: "Peptide Opioid Ligands (CNS-limited)",
    fullName: "Peptide opioid-receptor ligands with low CNS penetration",
    categoryId: "opioid", program: "Program D — Biased MOR / Safer Opioid",
    summary: "Peptide-based opioid research tools hypothesized to have lower diversion risk due to limited oral bioavailability and CNS penetration. Delivery is the primary research challenge.",
    flag: "Opioid-receptor similarity", benefit: 65, harm: 22
  }),
  "damgo-comparator": stub({
    id: "damgo-comparator", name: "DAMGO Comparator",
    fullName: "[D-Ala², N-MePhe⁴, Gly-ol]-enkephalin",
    categoryId: "opioid", program: "Program D — Biased MOR / Safer Opioid",
    summary: "Lab benchmark peptide — selective full MOR agonist standard used in binding/functional assays. Reference only, not a product target.",
    flag: "Opioid peptide benchmark", benefit: 50, harm: 30
  }),
  "u50488-comparator": stub({
    id: "u50488-comparator", name: "U-50,488 / KOR Comparator",
    fullName: "trans-(±)-3,4-Dichloro-N-methyl-N-[2-(1-pyrrolidinyl)cyclohexyl]benzeneacetamide",
    categoryId: "opioid", program: "Program D — Biased MOR / Safer Opioid",
    summary: "KOR-selective agonist lab benchmark compound. Reference tool for KOR pharmacology research — not a product target.",
    flag: "Opioid receptor similarity", benefit: 50, harm: 35
  }),

  // ========== IBOGA (missing) ==========
  "noribogaine": stub({
    id: "noribogaine", name: "Noribogaine",
    fullName: "Noribogaine (12-hydroxyibogamine)",
    categoryId: "iboga", program: "Program H — Iboga / Non-hallucinogenic Psychoplastogens",
    summary: "Ibogaine's primary active metabolite. Longer-acting and reported to lack ibogaine's cardiac arrhythmia risk — addiction and pain relevance.",
    flag: "Ibogaine-adjacent", benefit: 72, harm: 30
  }),
  "voacangine-analogs": stub({
    id: "voacangine-analogs", name: "Voacangine Analogs",
    fullName: "Voacangine-based iboga alkaloid research class",
    categoryId: "iboga", program: "Program H — Iboga / Non-hallucinogenic Psychoplastogens",
    summary: "Iboga alkaloid precursor / research class derived from Voacanga africana. Important for SAR exploration of the iboga pharmacophore.",
    flag: "Ibogaine-adjacent"
  }),
  "catharanthine-analogs": stub({
    id: "catharanthine-analogs", name: "Catharanthine Analogs",
    fullName: "Catharanthine-based iboga-like alkaloid research class",
    categoryId: "iboga", program: "Program H — Iboga / Non-hallucinogenic Psychoplastogens",
    summary: "Iboga-like alkaloid scaffold from Catharanthus roseus. Research tool for understanding the iboga/aspidosperma-type alkaloid structural class.",
    flag: "Ibogaine-adjacent"
  }),
  "coronaridine-analogs": stub({
    id: "coronaridine-analogs", name: "Coronaridine Analogs",
    fullName: "Coronaridine-based iboga addiction-research scaffold",
    categoryId: "iboga", program: "Program H — Iboga / Non-hallucinogenic Psychoplastogens",
    summary: "Related to 18-MC — coronaridine-based scaffold with addiction-research relevance and reduced cardiac risk vs ibogaine.",
    flag: "Ibogaine-adjacent", benefit: 70, harm: 22
  }),
  "psychoplastogen-analogs": stub({
    id: "psychoplastogen-analogs", name: "Non-Hallucinogenic Psychoplastogen Analogs",
    fullName: "TBG-like psychoplastogen expansion series",
    categoryId: "iboga", program: "Program H — Iboga / Non-hallucinogenic Psychoplastogens",
    summary: "Expansion class of TBG-like non-hallucinogenic psychoplastogens — 5-HT2A-biased neuroplasticity without perceptual effects.",
    flag: "Psychedelic/iboga-adjacent", benefit: 75, harm: 20
  }),

  // ========== DISSOCIATIVES / NMDA (missing) ==========
  "blixeprodil": stub({
    id: "blixeprodil", name: "Blixeprodil",
    fullName: "Blixeprodil — clinical-stage NMDAR modulator",
    categoryId: "dissociatives", program: "Program F — Ketamine / Glutamate",
    summary: "Clinical-stage ketamine-adjacent NMDAR modulator (GluN2B-selective) being investigated in depression and related indications.",
    flag: "Ketamine/glutamate-adjacent", benefit: 72, harm: 25
  }),
  "xw10508": stub({
    id: "xw10508", name: "XW10508",
    fullName: "XW10508 — oral esketamine prodrug",
    categoryId: "dissociatives", program: "Program F — Ketamine / Glutamate",
    summary: "Clinical-stage esketamine oral prodrug example. Reference compound for oral-delivery NMDA antagonist programs.",
    flag: "Ketamine/esketamine adjacency", benefit: 70, harm: 30
  }),
  "hnk-analogs": stub({
    id: "hnk-analogs", name: "HNK Analogs",
    fullName: "Hydroxynorketamine (HNK) analogs",
    categoryId: "dissociatives", program: "Program F — Ketamine / Glutamate",
    summary: "(2R,6R)-HNK is ketamine's active metabolite with antidepressant effects proposed to be NMDA-independent (AMPA-dependent). Potentially non-dissociative antidepressant pathway.",
    flag: "Ketamine metabolite adjacency", benefit: 78, harm: 18
  }),
  "norketamine-analogs": stub({
    id: "norketamine-analogs", name: "Norketamine Analogs",
    fullName: "Norketamine (ketamine N-demethyl metabolite) analog class",
    categoryId: "dissociatives", program: "Program F — Ketamine / Glutamate",
    summary: "Ketamine's primary metabolite with its own NMDA antagonist activity. Reference comparator class for understanding ketamine PK.",
    flag: "Ketamine adjacency", benefit: 65, harm: 30
  }),
  "mxe-comparator": stub({
    id: "mxe-comparator", name: "Methoxetamine (MXE) Comparator",
    fullName: "Methoxetamine — literature/risk comparator",
    categoryId: "dissociatives", program: "Program F — Ketamine / Glutamate",
    summary: "Literature/risk comparator — NOT a preferred target. Included for enforcement intelligence. Has been associated with overdose cases.",
    schedulingStatus: "scheduled",
    schedulingNote: "Schedule I in several countries (UK, EU). DEA temporarily scheduled; state-level varies. Research reference only.",
    flag: "Arylcyclohexylamine concern", benefit: 20, harm: 80
  }),
  "3meopcp-comparator": stub({
    id: "3meopcp-comparator", name: "3-MeO-PCP Comparator",
    fullName: "3-Methoxyphencyclidine — PCP-like risk comparator",
    categoryId: "dissociatives", program: "Program F — Ketamine / Glutamate",
    summary: "NOT A TARGET — PCP-like dissociative included only as a risk boundary / enforcement intelligence reference.",
    schedulingStatus: "scheduled",
    schedulingNote: "DO NOT SYNTHESIZE. Schedule I in multiple jurisdictions. High CSA similarity to PCP Schedule I/II. Reference only.",
    flag: "High Schedule I/II similarity", benefit: 5, harm: 95
  }),
  "memantine-analogs": stub({
    id: "memantine-analogs", name: "Memantine Analogs",
    fullName: "Memantine-derived NMDAR research analog class",
    categoryId: "dissociatives", program: "Program F — Ketamine / Glutamate",
    summary: "Lower-abuse NMDA research — memantine is an approved Alzheimer's drug with uncompetitive NMDA antagonism. Analogs are research tools for neuroprotection and cognition.",
    flag: "Approved-drug adjacency", benefit: 72, harm: 15
  }),
  "dxm-analogs": stub({
    id: "dxm-analogs", name: "Dextromethorphan Analogs",
    fullName: "DXM-derived NMDA/sigma antidepressant research analog class",
    categoryId: "dissociatives", program: "Program F — Ketamine / Glutamate",
    summary: "NMDA/sigma antidepressant mechanism research — parallel to the FDA-approved Auvelity (dextromethorphan + bupropion) for MDD.",
    flag: "DXM/pharma adjacency", benefit: 70, harm: 22
  }),
  "sigma-nmda-hybrids": stub({
    id: "sigma-nmda-hybrids", name: "Sigma-1 / NMDA Hybrid Tools",
    fullName: "Sigma-1 + NMDA receptor hybrid pharmacology research class",
    categoryId: "dissociatives", program: "Program F — Ketamine / Glutamate",
    summary: "Hybrid sigma-1 / NMDA modulators for depression, pain, and neuroprotection research. Lower ketamine similarity if structurally distinct.",
    benefit: 70, harm: 20
  }),
  "ampa-potentiators": stub({
    id: "ampa-potentiators", name: "AMPA Potentiator Adjuncts",
    fullName: "AMPA receptor potentiator research tools (ampakine class)",
    categoryId: "dissociatives", program: "Program F — Ketamine / Glutamate",
    summary: "Downstream glutamate pathway research — AMPA potentiators are thought to be downstream of ketamine's rapid antidepressant effect.",
    benefit: 70, harm: 15
  }),

  // ========== ENTACTOGENS (missing) ==========
  "mdai": stub({
    id: "mdai", name: "MDAI",
    fullName: "5,6-Methylenedioxy-2-aminoindane",
    categoryId: "entactogens", program: "Program E — Entactogens",
    summary: "Non-neurotoxic-entactogen literature candidate. Aminoindane scaffold reported with MDMA-like prosocial effects and lower serotonergic neurotoxicity in animal studies.",
    flag: "MDMA/MDA-adjacent", benefit: 72, harm: 28
  }),
  "5-iai": stub({
    id: "5-iai", name: "5-IAI",
    fullName: "5-Iodo-2-aminoindane",
    categoryId: "entactogens", program: "Program E — Entactogens",
    summary: "Aminoindane entactogen comparator — less-studied sibling to MDAI in the aminoindane class.",
    flag: "MDMA-adjacent", benefit: 65, harm: 30
  }),
  "mbdb-analogs": stub({
    id: "mbdb-analogs", name: "MBDB-like Analogs",
    fullName: "α-Ethyl homolog entactogen analog class",
    categoryId: "entactogens", program: "Program E — Entactogens",
    summary: "α-Ethyl homolog entactogen class (MBDB, BDB, etc.). Reference compounds for SERT-biased entactogen pharmacology.",
    flag: "MDMA/MDE/MBDB adjacency", benefit: 60, harm: 35
  }),
  "mde-analogs": stub({
    id: "mde-analogs", name: "MDE-like Analogs",
    fullName: "N-ethyl MDMA homolog entactogen class",
    categoryId: "entactogens", program: "Program E — Entactogens",
    summary: "N-ethyl homolog entactogen class. MDE is Schedule I — analogs are reference only.",
    schedulingStatus: "scheduled",
    schedulingNote: "MDE itself is Schedule I (CSA 1988). Analog Act consideration for any structurally-related research compound.",
    flag: "MDE is Schedule I", benefit: 40, harm: 60
  }),
  "sert-biased-releasers": stub({
    id: "sert-biased-releasers", name: "Non-Neurotoxic SERT-Biased Releasers",
    fullName: "SERT-selective monoamine releaser research class",
    categoryId: "entactogens", program: "Program E — Entactogens",
    summary: "Desired research class — SERT > DAT selectivity reduces MDMA-like neurotoxicity while preserving prosocial/entactogenic effects.",
    benefit: 78, harm: 20
  }),
  "low-dat-entactogens": stub({
    id: "low-dat-entactogens", name: "Low-DAT Entactogen Analogs",
    fullName: "Low dopamine transporter activity entactogen class",
    categoryId: "entactogens", program: "Program E — Entactogens",
    summary: "Entactogen analog class with reduced DAT activity hypothesized to lower abuse liability vs MDMA.",
    benefit: 75, harm: 22
  }),
  "oxytocin-adjuncts": stub({
    id: "oxytocin-adjuncts", name: "Oxytocin / Social-Bonding Adjuncts",
    fullName: "Oxytocin receptor agonist research adjuncts",
    categoryId: "entactogens", program: "Program E — Entactogens",
    summary: "Prosocial therapy research tools — non-MDMA mechanism. Oxytocin receptor agonism for social bonding research.",
    benefit: 72, harm: 12
  }),
  "ht1b-ht2a-social": stub({
    id: "ht1b-ht2a-social", name: "5-HT1B/5-HT2A Mixed Social-Behavior Tools",
    fullName: "Mixed serotonergic social-behavior research class",
    categoryId: "entactogens", program: "Program E — Entactogens",
    summary: "Mixed 5-HT1B/5-HT2A modulators for social behavior and fear-extinction research.",
    benefit: 68, harm: 18
  }),

  // ========== GABAERGIC / ANXIOLYTICS (missing) ==========
  "grx-917": stub({
    id: "grx-917", name: "GRX-917",
    fullName: "GRX-917 — deuterated etifoxine analog",
    categoryId: "gabaergic", program: "Anxiety Lane",
    summary: "Deuterated etifoxine-style anxiolytic from GRX Therapeutics (formerly GABA Therapeutics). In clinical development for anxiety disorders with TSPO/neurosteroid mechanism.",
    flag: "Etifoxine/pharma adjacency", benefit: 75, harm: 18
  }),
  "alpha-gaba-pam-library": stub({
    id: "alpha-gaba-pam-library", name: "α2/α3-Selective GABA-A PAM Library",
    fullName: "α2/α3-selective GABA-A positive allosteric modulator research class",
    categoryId: "gabaergic", program: "Anxiety Lane",
    summary: "Key Valium/Xanax replacement research concept — anxiolytic without α1-mediated sedation and dependence. KRM-II-81 is an exemplar.",
    flag: "Benzodiazepine-mechanism-adjacent", benefit: 80, harm: 22
  }),
  "tspo-ligands": stub({
    id: "tspo-ligands", name: "TSPO Ligands",
    fullName: "Translocator protein (TSPO) 18kDa ligand research class",
    categoryId: "gabaergic", program: "Anxiety Lane",
    summary: "Neurosteroidogenesis / anxiety / mood research tools — TSPO is the mitochondrial steroid-synthesis gatekeeper. Etifoxine's mechanism partly TSPO.",
    benefit: 70, harm: 15
  }),
  "allopregnanolone-analogs": stub({
    id: "allopregnanolone-analogs", name: "Allopregnanolone Analogs",
    fullName: "Allopregnanolone-derived neurosteroid GABA-A modulator class",
    categoryId: "gabaergic", program: "Anxiety Lane",
    summary: "Neurosteroid GABA-A modulation — brexanolone (Zulresso) and zuranolone (Zurzuvae) are approved allopregnanolone-based therapies for PPD and MDD.",
    flag: "Brexanolone/zuranolone pharma adjacency", benefit: 78, harm: 20
  }),
  "pregnenolone-analogs": stub({
    id: "pregnenolone-analogs", name: "Pregnenolone Analogs",
    fullName: "Pregnenolone precursor neurosteroid analog class",
    categoryId: "gabaergic", program: "Anxiety Lane",
    summary: "Neurosteroid precursor research — metabolized to allopregnanolone and other active neurosteroids.",
    benefit: 65, harm: 12
  }),
  "baclofen-analogs": stub({
    id: "baclofen-analogs", name: "Baclofen Analogs",
    fullName: "Baclofen-derived GABA-B agonist research class",
    categoryId: "gabaergic", program: "Anxiety Lane",
    summary: "GABA-B agonist research — baclofen is an approved muscle relaxant; analogs of interest for AUD and anxiety research.",
    flag: "Approved-drug adjacency", benefit: 68, harm: 22
  }),
  "pagoclone-like": stub({
    id: "pagoclone-like", name: "Pagoclone-like Compounds",
    fullName: "Pagoclone and cyclopyrrolone non-benzodiazepine anxiolytic class",
    categoryId: "gabaergic", program: "Anxiety Lane",
    summary: "Non-benzo anxiolytic class — cyclopyrrolone scaffold binding at benzodiazepine site with potentially lower abuse profile.",
    flag: "Z-drug/benzo-site adjacency", benefit: 65, harm: 28
  }),
  "afobazole-analogs": stub({
    id: "afobazole-analogs", name: "Fabomotizole / Afobazole Analogs",
    fullName: "Fabomotizole (Afobazole) sigma/MT anxiolytic research class",
    categoryId: "gabaergic", program: "Anxiety Lane",
    summary: "Sigma-1/MT anxiolytic class — Afobazole approved in Russia for anxiety; analogs for research.",
    benefit: 70, harm: 15
  }),
  "kava-lactone-analogs": stub({
    id: "kava-lactone-analogs", name: "Kava Lactone Analogs",
    fullName: "Kavalactone (e.g., kavain, dihydrokavain) analog research class",
    categoryId: "gabaergic", program: "Anxiety Lane",
    summary: "Botanical anxiolytic research — kavalactones from Piper methysticum with mixed GABA-A modulation.",
    benefit: 65, harm: 25
  }),
  "l-theanine-analogs": stub({
    id: "l-theanine-analogs", name: "L-Theanine Analogs",
    fullName: "L-Theanine-derived low-risk anxiolytic/nootropic research class",
    categoryId: "gabaergic", program: "Anxiety Lane",
    summary: "Low-risk anxiolytic/nootropic research — L-theanine derivatives for anxiety reduction and cognitive support.",
    benefit: 62, harm: 5
  }),

  // ========== COGNITIVE / WAKEFULNESS (missing) ==========
  "modafinil-comparator": stub({
    id: "modafinil-comparator", name: "Modafinil Comparator",
    fullName: "Modafinil — FDA-approved wakefulness drug (reference)",
    aliases: ["Provigil"],
    categoryId: "cognitive", program: "Program C — Wakefulness / Neuroinflammation",
    summary: "Approved Schedule IV wakefulness drug. Reference compound only — essential benchmark for Wohler's F-modafinil and modafinil derivative programs.",
    schedulingStatus: "scheduled",
    schedulingNote: "SCHEDULE IV (DEA). FDA-approved for narcolepsy, shift-work sleep disorder, and OSA.",
    flag: "Schedule IV", benefit: 60, harm: 35
  }),
  "armodafinil-comparator": stub({
    id: "armodafinil-comparator", name: "Armodafinil Comparator",
    fullName: "Armodafinil — (R)-enantiomer of modafinil (reference)",
    aliases: ["Nuvigil"],
    categoryId: "cognitive", program: "Program C — Wakefulness / Neuroinflammation",
    summary: "Approved Schedule IV (R)-enantiomer of modafinil with longer duration. Reference compound only.",
    schedulingStatus: "scheduled",
    schedulingNote: "SCHEDULE IV (DEA). FDA-approved.",
    flag: "Schedule IV", benefit: 60, harm: 35
  }),
  "flmodafinil-lauflumide": stub({
    id: "flmodafinil-lauflumide", name: "Flmodafinil / Lauflumide",
    fullName: "Bisfluoromodafinil (CRL-40,940)",
    aliases: ["CRL-40940", "Lauflumide"],
    categoryId: "cognitive", program: "Program C — Wakefulness / Neuroinflammation",
    summary: "Bis-fluoro modafinil analog — research wakefulness compound. Same scaffold as F-modafinil program but with two fluorines.",
    flag: "Modafinil adjacency", benefit: 70, harm: 28
  }),
  "fladrafinil": stub({
    id: "fladrafinil", name: "Fladrafinil",
    fullName: "Fladrafinil (CRL-40,941)",
    aliases: ["CRL-40941"],
    categoryId: "cognitive", program: "Program C — Wakefulness / Neuroinflammation",
    summary: "Bis-fluoro adrafinil analog — the fluorinated prodrug to flmodafinil. Research wakefulness compound.",
    flag: "Modafinil/adrafinil adjacency", benefit: 65, harm: 30
  }),
  "ce-123": stub({
    id: "ce-123", name: "CE-123",
    fullName: "CE-123 — atypical DAT inhibitor",
    categoryId: "cognitive", program: "Program C — Wakefulness / Neuroinflammation",
    summary: "Atypical dopamine transporter inhibitor / nootropic candidate from the Sitte lab. Pro-cognitive effects without classical stimulant abuse liability.",
    flag: "Stimulant-mechanism adjacency", benefit: 72, harm: 25
  }),
  "bromantane-analogs": stub({
    id: "bromantane-analogs", name: "Bromantane Analogs",
    fullName: "Bromantane-derived adamantane wakefulness research class",
    categoryId: "cognitive", program: "Program C — Wakefulness / Neuroinflammation",
    summary: "Adamantane dopaminergic/adaptogen/wakefulness research class — Bromantane approved in Russia for asthenic disorders.",
    flag: "Stimulant-adjacent", benefit: 65, harm: 22
  }),
  "alpha7-nachr-pams": stub({
    id: "alpha7-nachr-pams", name: "α7 Nicotinic PAMs",
    fullName: "α7 nicotinic acetylcholine receptor positive allosteric modulators",
    categoryId: "cognitive", program: "Program J — Nootropic / Cognitive",
    summary: "Focus/cognition/inflammation research — α7 nAChR PAMs without direct nicotine agonism.",
    benefit: 70, harm: 15
  }),
  "ampakines": stub({
    id: "ampakines", name: "Ampakines",
    fullName: "AMPA receptor positive allosteric modulator research class",
    categoryId: "cognitive", program: "Program J — Nootropic / Cognitive",
    summary: "AMPA potentiation / cognition research class — sunifiram and unifiram are exemplars. No direct CSA similarity.",
    benefit: 72, harm: 12
  }),
  "pde4-cognition-tools": stub({
    id: "pde4-cognition-tools", name: "PDE4 Inhibitors / Roflumilast-like",
    fullName: "PDE4 inhibitor cognition/neuroinflammation research class",
    categoryId: "cognitive", program: "Program C — Wakefulness / Neuroinflammation",
    summary: "cAMP / cognition / inflammation research — PDE4 inhibition. Roflumilast is an approved COPD drug; research analogs of interest for cognition.",
    flag: "Approved-drug adjacency", benefit: 68, harm: 18
  }),
  "h3-antagonists": stub({
    id: "h3-antagonists", name: "Histamine H3 Antagonists",
    fullName: "Histamine H3 receptor antagonist/inverse agonist research class",
    categoryId: "cognitive", program: "Program C — Wakefulness / Neuroinflammation",
    summary: "Wakefulness/cognition research — pitolisant is approved (Wakix) for narcolepsy. H3 antagonism increases histamine, ACh, NE, DA.",
    flag: "Pharma adjacency", benefit: 70, harm: 18
  }),
  "orexin-agonists": stub({
    id: "orexin-agonists", name: "Orexin Agonist Research Tools",
    fullName: "Orexin receptor agonist research compounds",
    categoryId: "cognitive", program: "Program C — Wakefulness / Neuroinflammation",
    summary: "Narcolepsy/wakefulness research — orexin agonism being pursued by Takeda (TAK-861) and others for narcolepsy type 1.",
    flag: "Pharma adjacency", benefit: 75, harm: 18
  }),

  // ========== PAIN RESEARCH COMPOUNDS (missing) ==========
  "2mm-salvinorin-b": stub({
    id: "2mm-salvinorin-b", name: "2-Methoxymethyl Salvinorin B",
    fullName: "2-Methoxymethyl salvinorin B",
    categoryId: "pain", program: "Pain / Non-Opioid Analgesia",
    summary: "Salvinorin B analog with enhanced KOR potency/duration — analgesia / itch / inflammation research tool.",
    flag: "Salvinorin-adjacent", benefit: 70, harm: 25
  }),
  "eom-salvinorin-b": stub({
    id: "eom-salvinorin-b", name: "EOM-Salvinorin B",
    fullName: "Ethoxymethyl salvinorin B",
    categoryId: "pain", program: "Pain / Non-Opioid Analgesia",
    summary: "Ethoxymethyl salvinorin B analog — KOR research tool with extended duration vs parent salvinorin A.",
    flag: "Salvinorin-adjacent", benefit: 70, harm: 25
  }),
  "sigma-2-ligands": stub({
    id: "sigma-2-ligands", name: "Sigma-2 Receptor Ligands",
    fullName: "Sigma-2 (TMEM97) receptor ligand research class",
    categoryId: "pain", program: "Pain / Non-Opioid Analgesia",
    summary: "Pain/inflammation/cancer research — sigma-2 receptor (TMEM97) is a novel pain target.",
    benefit: 72, harm: 12
  }),
  "nav17-blockers": stub({
    id: "nav17-blockers", name: "NaV1.7 Blockers",
    fullName: "Voltage-gated sodium channel 1.7 selective blocker class",
    categoryId: "pain", program: "Pain / Non-Opioid Analgesia",
    summary: "Non-opioid pain research — NaV1.7 is the 'pain channel' genetically validated for analgesia without CNS effects.",
    benefit: 80, harm: 10
  }),
  "nav18-blockers": stub({
    id: "nav18-blockers", name: "NaV1.8 Blockers",
    fullName: "Voltage-gated sodium channel 1.8 selective blocker class",
    categoryId: "pain", program: "Pain / Non-Opioid Analgesia",
    summary: "Non-opioid pain research — highly relevant post-Journavx (suzetrigine) era. FDA-approved Jan 2025 for acute pain.",
    benefit: 85, harm: 10
  }),
  "trpv1-antagonists": stub({
    id: "trpv1-antagonists", name: "TRPV1 Antagonists",
    fullName: "Transient receptor potential V1 antagonist class",
    categoryId: "pain", program: "Pain / Non-Opioid Analgesia",
    summary: "Pain / inflammation research — TRPV1 is the capsaicin receptor; antagonists of interest for neuropathic pain.",
    benefit: 68, harm: 18
  }),
  "trpa1-antagonists": stub({
    id: "trpa1-antagonists", name: "TRPA1 Antagonists",
    fullName: "Transient receptor potential A1 antagonist class",
    categoryId: "pain", program: "Pain / Non-Opioid Analgesia",
    summary: "Pain/inflammation research — TRPA1 is a cold and irritant receptor involved in inflammatory pain.",
    benefit: 68, harm: 15
  }),
  "p2x3-antagonists": stub({
    id: "p2x3-antagonists", name: "P2X3 Antagonists",
    fullName: "P2X3 purinergic receptor antagonist class",
    categoryId: "pain", program: "Pain / Non-Opioid Analgesia",
    summary: "Pain/cough/sensory signaling research — gefapixant is an approved P2X3 antagonist for chronic cough.",
    benefit: 70, harm: 15
  }),
  "n-ca-blockers": stub({
    id: "n-ca-blockers", name: "N-type Calcium Channel Blockers",
    fullName: "N-type (Cav2.2) calcium channel blocker pain research class",
    categoryId: "pain", program: "Pain / Non-Opioid Analgesia",
    summary: "Pain research — ziconotide (Prialt) is an approved intrathecal ω-conotoxin; research analogs explore oral/systemic delivery.",
    flag: "Approved-drug adjacency", benefit: 65, harm: 20
  }),
  "faah-inhibitors": stub({
    id: "faah-inhibitors", name: "FAAH Inhibitors",
    fullName: "Fatty acid amide hydrolase inhibitor research class",
    categoryId: "pain", program: "Pain / Non-Opioid Analgesia",
    summary: "Endocannabinoid pain modulation — FAAH inhibition raises anandamide tone for pain and anxiety research.",
    flag: "Endocannabinoid-system adjacency", benefit: 72, harm: 15
  }),
  "magl-inhibitors": stub({
    id: "magl-inhibitors", name: "MAGL Inhibitors",
    fullName: "Monoacylglycerol lipase inhibitor research class",
    categoryId: "pain", program: "Pain / Non-Opioid Analgesia",
    summary: "Endocannabinoid pain modulation — MAGL inhibition raises 2-AG tone. Research tools for pain and inflammation.",
    flag: "Endocannabinoid-system adjacency", benefit: 70, harm: 18
  }),
  "pea-analogs": stub({
    id: "pea-analogs", name: "Palmitoylethanolamide (PEA) Analogs",
    fullName: "PEA and PPAR-α anti-inflammatory pain research class",
    categoryId: "pain", program: "Pain / Non-Opioid Analgesia",
    summary: "PPAR-alpha / anti-inflammatory pain research — PEA is an endogenous fatty-acid amide with analgesic and anti-inflammatory activity.",
    benefit: 65, harm: 5
  }),
  "ldn-analogs": stub({
    id: "ldn-analogs", name: "Low-Dose Naltrexone Analogs",
    fullName: "Low-dose naltrexone-derived glial/TLR4 modulator class",
    categoryId: "pain", program: "Pain / Non-Opioid Analgesia",
    summary: "Glial / TLR4 / pain research — low-dose naltrexone is used off-label for chronic pain and fibromyalgia; analogs for research.",
    flag: "Opioid antagonist drug adjacency", benefit: 70, harm: 12
  }),

  // ========== ED / SEXUAL HEALTH (missing) ==========
  "tadalafil-analogs-class": stub({
    id: "tadalafil-analogs-class", name: "Tadalafil Analog Class",
    fullName: "Tadalafil structural analog class for PDE5/PDE11 selectivity research",
    categoryId: "ed-sexual", program: "Program G — ED / Sexual Health",
    summary: "Broader tadalafil analog class beyond amino- and thio-tadalafil. PDE5 / PDE11 selectivity SAR research.",
    flag: "Approved-drug adjacency", benefit: 70, harm: 22
  }),
  "avanafil-analogs": stub({
    id: "avanafil-analogs", name: "Avanafil Analogs",
    fullName: "Avanafil-derived fast-onset PDE5 analog class",
    categoryId: "ed-sexual", program: "Program G — ED / Sexual Health",
    summary: "Fast-onset PDE5 research — avanafil (Stendra) has the fastest onset of approved PDE5 inhibitors.",
    flag: "Approved-drug adjacency", benefit: 68, harm: 22
  }),
  "mirodenafil": stub({
    id: "mirodenafil", name: "Mirodenafil",
    fullName: "Mirodenafil — potent/selective PDE5 inhibitor",
    categoryId: "ed-sexual", program: "Program G — ED / Sexual Health",
    summary: "Potent/selective PDE5 comparator — approved in South Korea for ED. Research reference for PDE5 selectivity.",
    flag: "Approved-drug adjacency", benefit: 68, harm: 22
  }),
  "udenafil": stub({
    id: "udenafil", name: "Udenafil",
    fullName: "Udenafil — long-duration PDE5 inhibitor",
    categoryId: "ed-sexual", program: "Program G — ED / Sexual Health",
    summary: "Duration/onset PDE5 comparator — approved in South Korea. Longer duration than sildenafil.",
    flag: "Approved-drug adjacency", benefit: 68, harm: 22
  }),
  "lodenafil": stub({
    id: "lodenafil", name: "Lodenafil Carbonate",
    fullName: "Lodenafil carbonate — PDE5 prodrug",
    categoryId: "ed-sexual", program: "Program G — ED / Sexual Health",
    summary: "PDE5 prodrug comparator — approved in Brazil for ED. Prodrug PK research reference.",
    flag: "Approved-drug adjacency", benefit: 65, harm: 22
  }),
  "slx-2101": stub({
    id: "slx-2101", name: "SLx-2101",
    fullName: "SLx-2101 — long-acting PDE5 candidate",
    categoryId: "ed-sexual", program: "Program G — ED / Sexual Health",
    summary: "Long-acting PDE5 candidate from Surface Logix — research reference for extended-duration PDE5 research.",
    flag: "Approved-drug/development adjacency", benefit: 65, harm: 22
  }),
  "tpn171h": stub({
    id: "tpn171h", name: "TPN171H / Simmerafil",
    fullName: "TPN171H (Simmerafil) — PDE5 ED/PAH candidate",
    categoryId: "ed-sexual", program: "Program G — ED / Sexual Health",
    summary: "PDE5 ED / pulmonary arterial hypertension candidate — Chinese development-stage compound.",
    flag: "Approved-drug/development adjacency", benefit: 68, harm: 22
  }),
  "icariside-ii": stub({
    id: "icariside-ii", name: "Icariside II",
    fullName: "Icariside II — Epimedium-derived icariin metabolite",
    categoryId: "ed-sexual", program: "Program G — ED / Sexual Health",
    summary: "Stronger icariin metabolite candidate — more potent PDE5 inhibition than parent icariin in published studies.",
    benefit: 72, harm: 10
  }),
  "baohuoside-analogs": stub({
    id: "baohuoside-analogs", name: "Baohuoside Analogs",
    fullName: "Baohuoside icariin-family SAR research class",
    categoryId: "ed-sexual", program: "Program G — ED / Sexual Health",
    summary: "Icariin-family SAR — baohuoside is a key Epimedium bioactive flavonoid with PDE5 activity.",
    benefit: 68, harm: 8
  }),
  "melanotan-ii": stub({
    id: "melanotan-ii", name: "Melanotan II",
    fullName: "Melanotan II — MC1R/MC3R/MC4R/MC5R agonist peptide",
    categoryId: "ed-sexual", program: "Program G — ED / Sexual Health",
    summary: "MC receptor sexual arousal comparator — non-selective melanocortin agonist. FDA has issued consumer warnings; research-use-only.",
    flag: "Peptide/drug adjacency", benefit: 60, harm: 35
  }),
  "oxytocin-analogs": stub({
    id: "oxytocin-analogs", name: "Oxytocin Analogs",
    fullName: "Oxytocin receptor agonist research peptide class",
    categoryId: "ed-sexual", program: "Program G — ED / Sexual Health",
    summary: "Bonding / arousal research — oxytocin analog research for sexual-health and relationship research.",
    flag: "Peptide/drug adjacency", benefit: 65, harm: 10
  }),
  "no-donor-adjuncts": stub({
    id: "no-donor-adjuncts", name: "NO Donor Adjuncts",
    fullName: "Nitric oxide donor research class",
    categoryId: "ed-sexual", program: "Program G — ED / Sexual Health",
    summary: "Low-NO ED / diabetic ED research — NO donor adjuncts to PDE5 inhibitor therapy.",
    benefit: 65, harm: 12
  }),
  "sgc-stimulators": stub({
    id: "sgc-stimulators", name: "sGC Stimulators / Modulators",
    fullName: "Soluble guanylate cyclase stimulator research class",
    categoryId: "ed-sexual", program: "Program G — ED / Sexual Health",
    summary: "NO/cGMP pathway for PDE5 nonresponders — sGC stimulators like riociguat (Adempas) approved for PAH.",
    flag: "Approved-drug adjacency", benefit: 68, harm: 18
  }),
  "rhok-inhibitors": stub({
    id: "rhok-inhibitors", name: "Rho-Kinase Inhibitors",
    fullName: "Rho-associated kinase (ROCK) inhibitor smooth-muscle research class",
    categoryId: "ed-sexual", program: "Program G — ED / Sexual Health",
    summary: "Smooth-muscle relaxation / ED research — Rho-kinase inhibition enhances NO-cGMP pathway.",
    benefit: 65, harm: 15
  }),
  "endothelin-antagonists": stub({
    id: "endothelin-antagonists", name: "Endothelin Antagonists",
    fullName: "Endothelin receptor antagonist vascular research class",
    categoryId: "ed-sexual", program: "Program G — ED / Sexual Health",
    summary: "Vascular ED research — endothelin-1 is a potent vasoconstrictor; antagonists for vascular conditions.",
    flag: "Approved-drug adjacency", benefit: 62, harm: 22
  }),
  "d4-agonists": stub({
    id: "d4-agonists", name: "Dopamine D4 Agonist Analogs",
    fullName: "Dopamine D4 receptor selective agonist class",
    categoryId: "ed-sexual", program: "Program G — ED / Sexual Health",
    summary: "Central arousal pathway research — D4 agonists for sexual function without D2-mediated side effects.",
    flag: "Dopaminergic risk, not CSA by default", benefit: 65, harm: 22
  }),

  // ========== PEPTIDES / LONGEVITY (missing) ==========
  "epithalamin-peptides": stub({
    id: "epithalamin-peptides", name: "Epithalamin-Derived Peptides",
    fullName: "Epithalamin / pineal peptide research class",
    categoryId: "peptides", program: "Program I — Peptide PK-Extension / Epitalon",
    summary: "Pineal peptide research — epithalamin is a mixture of low-MW pineal peptides; Epitalon is the simplified tetrapeptide.",
    benefit: 70, harm: 8
  }),
  "epitalon-hl-extended": stub({
    id: "epitalon-hl-extended", name: "Half-Life Extended Epitalon Analogs",
    fullName: "Epitalon PK-extension analog class",
    categoryId: "peptides", program: "Program I — Peptide PK-Extension / Epitalon",
    summary: "IP/patentable peptide PK project — Epitalon has <1h half-life in plasma; HL-extended analogs are a major Wohler research opportunity.",
    benefit: 78, harm: 8
  }),
  "epitalon-lipidated": stub({
    id: "epitalon-lipidated", name: "Lipidated Epitalon Analogs",
    fullName: "Epitalon lipid-conjugated PK-extension analog class",
    categoryId: "peptides", program: "Program I — Peptide PK-Extension / Epitalon",
    summary: "GLP-1-style half-life extension concept — lipid conjugation (like semaglutide) applied to Epitalon for weekly dosing potential.",
    benefit: 78, harm: 10
  }),
  "epitalon-albumin-binding": stub({
    id: "epitalon-albumin-binding", name: "Albumin-Binding Epitalon Analogs",
    fullName: "Epitalon albumin-binding HL-extension analog class",
    categoryId: "peptides", program: "Program I — Peptide PK-Extension / Epitalon",
    summary: "Half-life extension via albumin binding — fatty-acid / albumin-binder conjugation strategy.",
    benefit: 75, harm: 10
  }),
  "epitalon-pegylated": stub({
    id: "epitalon-pegylated", name: "PEGylated Epitalon Analogs",
    fullName: "PEGylated Epitalon PK-extension analog class",
    categoryId: "peptides", program: "Program I — Peptide PK-Extension / Epitalon",
    summary: "Half-life extension via PEGylation — classical peptide PK-extension strategy.",
    benefit: 72, harm: 10
  }),
  "cjc-1295-comparator": stub({
    id: "cjc-1295-comparator", name: "CJC-1295-like Comparators",
    fullName: "CJC-1295 and DAC-technology peptide PK research comparators",
    categoryId: "peptides", program: "Program I — Peptide PK-Extension / Epitalon",
    summary: "Peptide PK research — CJC-1295's DAC (drug-affinity complex) technology is a reference for albumin-binding peptide half-life extension.",
    flag: "Peptide/drug adjacency", benefit: 68, harm: 15
  }),
  "melatonin-peptide-hybrids": stub({
    id: "melatonin-peptide-hybrids", name: "Melatonin Peptide Hybrids",
    fullName: "Melatonin-peptide conjugate research class",
    categoryId: "peptides", program: "Program I — Peptide PK-Extension / Epitalon",
    summary: "Sleep / circadian research — melatonin conjugated to Epitalon-like peptides for dual circadian mechanisms.",
    benefit: 70, harm: 8
  }),
  "dsip-analogs": stub({
    id: "dsip-analogs", name: "DSIP Analogs",
    fullName: "Delta sleep-inducing peptide analog research class",
    categoryId: "peptides", program: "Program I — Peptide PK-Extension / Epitalon",
    summary: "Sleep peptide research — DSIP is a 9-amino-acid sleep-regulating peptide isolated from rabbit brain.",
    benefit: 65, harm: 10
  }),
  "bpc-157": stub({
    id: "bpc-157", name: "BPC-157 Analogs",
    fullName: "BPC-157 — Body Protective Compound-157 research class",
    categoryId: "peptides", program: "Program I — Peptide PK-Extension / Epitalon",
    summary: "Tissue-repair research — BPC-157 is a 15-amino-acid peptide derived from human gastric juice. FDA has issued cautions on compounded use; research-use-only posture required.",
    flag: "Peptide/drug/claim risk", benefit: 68, harm: 22
  }),
  "thymosin-beta4": stub({
    id: "thymosin-beta4", name: "Thymosin β-4 Fragments",
    fullName: "Thymosin β-4 fragment research class",
    categoryId: "peptides", program: "Program I — Peptide PK-Extension / Epitalon",
    summary: "Tissue repair / inflammation research — thymosin β-4 fragment research. Regulatory caution on wellness / clinical claims.",
    flag: "Peptide/drug/claim risk", benefit: 65, harm: 18
  }),
  "kpv-analogs": stub({
    id: "kpv-analogs", name: "KPV Peptide Analogs",
    fullName: "α-MSH-derived Lys-Pro-Val tripeptide anti-inflammatory research class",
    categoryId: "peptides", program: "Program I — Peptide PK-Extension / Epitalon",
    summary: "Anti-inflammatory peptide research — KPV is a tripeptide fragment of α-MSH with anti-inflammatory activity.",
    benefit: 65, harm: 8
  }),
  "semax-selank": stub({
    id: "semax-selank", name: "Semax / Selank Analogs",
    fullName: "Semax and Selank Russian nootropic/anxiolytic peptide research class",
    categoryId: "peptides", program: "Program I — Peptide PK-Extension / Epitalon",
    summary: "Nootropic / anxiolytic peptides — developed in Russia. Semax is an ACTH fragment; Selank is a tuftsin analog.",
    benefit: 68, harm: 10
  }),
  "glp1-platform": stub({
    id: "glp1-platform", name: "GLP-1 / GIP Half-Life Platform",
    fullName: "GLP-1 / GIP technology platform for peptide PK extension",
    categoryId: "peptides", program: "Program I — Peptide PK-Extension / Epitalon",
    summary: "Not an RC lead — platform technology reference. Semaglutide/tirzepatide PK-extension technology applicable to Epitalon and other peptides.",
    flag: "Approved-drug adjacency", benefit: 55, harm: 8
  }),

  // ========== CANNABINOID / CB2 (missing) ==========
  "cb2-selective-class": stub({
    id: "cb2-selective-class", name: "CB2-Selective Agonist Class",
    fullName: "CB2-selective cannabinoid agonist research class",
    categoryId: "cannabinoid", program: "Program K — Endocannabinoid / CB2",
    summary: "Pain / inflammation research without CB1 intoxication. Class entry beyond HU-308 and JWH-133 — includes novel scaffolds in development.",
    flag: "Cannabinoid-system adjacency", benefit: 75, harm: 15
  }),
  "beta-caryophyllene-analogs": stub({
    id: "beta-caryophyllene-analogs", name: "β-Caryophyllene Analogs",
    fullName: "β-Caryophyllene natural CB2 agonist analog class",
    categoryId: "cannabinoid", program: "Program K — Endocannabinoid / CB2",
    summary: "Natural CB2 agonist scaffold — β-caryophyllene is a sesquiterpene found in cloves, black pepper, and cannabis. FDA GRAS-listed.",
    benefit: 70, harm: 5
  }),
  "cbd-analogs-class": stub({
    id: "cbd-analogs-class", name: "CBD Analog Class",
    fullName: "Cannabidiol structural analog research class (beyond CBD itself)",
    categoryId: "cannabinoid", program: "Program K — Endocannabinoid / CB2",
    summary: "Anti-inflammatory / anxiety / pain research — structural analogs of CBD including various alkyl-chain variants.",
    flag: "Cannabis adjacency but lower THC risk", benefit: 75, harm: 12
  }),
  "cbg-analogs-class": stub({
    id: "cbg-analogs-class", name: "CBG Analog Class",
    fullName: "Cannabigerol structural analog research class",
    categoryId: "cannabinoid", program: "Program K — Endocannabinoid / CB2",
    summary: "Inflammation, pain, neuroprotection research — structural analogs of CBG beyond the natural compound.",
    flag: "Cannabis adjacency", benefit: 72, harm: 12
  }),
  "cbc-analogs": stub({
    id: "cbc-analogs", name: "CBC Analogs",
    fullName: "Cannabichromene structural analog class",
    categoryId: "cannabinoid", program: "Program K — Endocannabinoid / CB2",
    summary: "Inflammation / pain / neurogenesis research — CBC is a non-intoxicating phytocannabinoid with TRP and CB2 activity.",
    flag: "Cannabis adjacency", benefit: 68, harm: 10
  }),
  "cbdv-analogs": stub({
    id: "cbdv-analogs", name: "CBDV Analogs",
    fullName: "Cannabidivarin structural analog class",
    categoryId: "cannabinoid", program: "Program K — Endocannabinoid / CB2",
    summary: "Seizure / autism / inflammation research — CBDV is a propyl-chain CBD variant. GW Pharma ran Phase 2 for autism.",
    flag: "Cannabis adjacency", benefit: 72, harm: 10
  }),
  "thcv-analogs": stub({
    id: "thcv-analogs", name: "THCV Analogs",
    fullName: "Tetrahydrocannabivarin structural analog class",
    categoryId: "cannabinoid", program: "Program K — Endocannabinoid / CB2",
    summary: "Metabolic / appetite / cognition research — THCV is the propyl-chain THC variant with unique pharmacology (CB1 antagonist at low dose, agonist at high dose).",
    schedulingStatus: "monitor",
    schedulingNote: "THC structural caution — THCV is federally unscheduled if hemp-derived <0.3% THC but state laws vary significantly. Confirm per state.",
    flag: "THC structural caution", benefit: 65, harm: 25
  }),
  "naaa-inhibitors": stub({
    id: "naaa-inhibitors", name: "NAAA Inhibitors",
    fullName: "N-acylethanolamine acid amidase inhibitor research class",
    categoryId: "cannabinoid", program: "Program K — Endocannabinoid / CB2",
    summary: "PEA / endocannabinoid-like anti-inflammatory pathway — NAAA inhibition raises PEA tone.",
    benefit: 68, harm: 8
  }),
  "oea-analogs": stub({
    id: "oea-analogs", name: "OEA Analogs",
    fullName: "Oleoylethanolamide analog research class",
    categoryId: "cannabinoid", program: "Program K — Endocannabinoid / CB2",
    summary: "Appetite / metabolic / inflammation research — OEA is an endogenous PPAR-α agonist related to the endocannabinoid family.",
    benefit: 65, harm: 8
  }),
  "non-cb1-modulators": stub({
    id: "non-cb1-modulators", name: "Non-CB1 Cannabinoid Modulators",
    fullName: "Non-CB1 cannabinoid receptor modulator research class",
    categoryId: "cannabinoid", program: "Program K — Endocannabinoid / CB2",
    summary: "Lower-intoxication cannabinoid program — cannabinoid modulators that spare CB1 (avoiding the THC-like high).",
    flag: "Lower than THC-like compounds", benefit: 72, harm: 12
  })
};